import { EOfferType } from '@/enums';
import { IOffersItem } from '@/types';
import Vue from 'vue';
import Vuex from 'vuex';
import Helpers, { tokenExpiryBufferMs } from '@/mixins/helpers';
import { jwtDecode } from 'jwt-decode';
import router from '@/router';
import { FORGEROCK_ENABLED } from '@/featureToggles';
import {
	blankQuoteCreate,
	blankUpdateQuote,
	blankUpdateRepayments,
	blankRetrieveQuote,
	blankLastSavedQuote
} from '@/store/blanks';
import {
	getQuoteCreateObj,
	getUpdateRepaymentsObj,
	getAlphaFinance,
	getUseAlpha,
	getAlphaOrderOverrides,
	hasQuoteConflict,
	hasRepaymentConflict,
	isUsableNumber,
	getRetrievedFinanceSettings
} from '@/mixins/quoteHelpers';
import {
	updateQuoteApi,
	updateRepaymentsApi,
	retrieveQuoteApi,
	lastSavedQuote
} from '@/mixins/quoteApi';

Vue.use(Vuex)

// every `isFinanceLoading: true` must be paired with a `false`; the flag only
// clears once all outstanding loaders have finished
function applyFinanceLoading(state: any, loading: boolean) {
	const count = state.app.financeLoadingCount || 0;
	state.app.financeLoadingCount = loading ? count + 1 : Math.max(0, count - 1);
	state.app.isFinanceLoading = state.app.financeLoadingCount > 0;
}

// expiry timer for the ForgeRock session (fires bufferMs before real expiry)
let forgeRockExpiryTimer: any = 0;

export default new Vuex.Store({
	state: {
		app: {
			currentRouteName: '',
			useEstimateInsteadOfAlpha: false,
			// the selection (getSelectionSignature) the saved quote is known to
			// match: set after a successful UpdateRepayments or a conflict-free
			// retrieve, cleared when a save fails or a new quote is created.
			// getUseAlpha compares it with the current selection, so any car /
			// finance change drops the page to the indicative estimate until
			// the next save point, which also skips UpdateQuote / UpdateRepayments
			// while the selection is unchanged (App.doCheckForConflicts)
			alphaSelection: '',
			// user asked to see the saved (retrieved) quote's figures after an
			// UpdateRepayments failure dropped the page to the estimate (tpr parity)
			viewCurrentQuote: false,
			isPostingTpr: false,
			isCustomerPhoneValidated: false,
			hasBadOmniGuid: false,
			// ForgeRock feature toggle, see src/featureToggles.ts. Off: no OTP
			// modal, no Check route, no token-expiry redirect, no OTP gate on TprForm
			forgerockEnabled: FORGEROCK_ENABLED,
            isOtpModalOpen: false,
			disableDisclaimer: false,
			showValueAdd: true,
			paid: false,
			financeLeadOrderNumber: '',
			isFinanceAvailable: false,
			isFinanceLoading: false,
			// isFinanceLoading is reference-counted (see applyFinanceLoading): nested
			// loaders, e.g. save-quote wrapping retrieve/update/repayments, keep it true
			// until the outermost one finishes
			financeLoadingCount: 0,
			referenceEnabled: false,
			hasActiveReferenceCodes: false,
			webkiosk: false,
			loginLogged: false,
			step: 0,
			changeCarUrl: '',
			referrer: '',
			cashDeposit: 100,
			cashDepositBackup: 100,

			financeType: 1,
			originalFinanceType: 1,
			financeDeposit: 0,
			loanTerm: 1,
			loanTermBackup: 1,
			kmsPerYear: 10000,
			kmsPerYearBackup: 10000,
            balloonPercentage: 0,

			financePayToday: 0,
			myToyotaLink: 'https://www.toyota.com.au/mytoyota-home',
			orderNumber: '',
			orderGuid: '',
			orderHash: '',
			session: '',
			myToyotaId: '',
			hasCompletedWithMyToyotaId: false,
			sessionTimeout: 600,
			paymentMethod: 'finance',
			tprSource: '',
			tprFormSubmitted: false,
			enquirySource: {
				id: null,
				suburb: null,
				value: null
			}
		},
		valueAdd: {
			hasSentAnalytics: false,
			hasRetrievedData: false,
			data: []
		},
		vehicle: {
			materialCode: '',
			specialUniqueName: '',
			vehicleSpecificationID: 0,
			variantCode: '',
			batchNumber: '',
			image: '',
			images360: [],
			franchise: 'Toyota',
			model: '',
			condition: 'Brand New',
			vin: '',
			grade: '',
			gradeLevelDescription: '',
			type: 'New Virtual',

			special: '',
			specialItem: null,
			specialExtraInfo: '',
			hasNationalSpecial: false,

			price: 0,
			transmission: '',
			fuel: '',
			bodyColour: '',
			interiorColour: '',
			inclOption: '',
			canBeFinanced: false,
			financeFailureReason: '',
			fullDescription: '',
			strippedFullDescription: '',
			initialFinanceSetting: {
				termInYears: null
			},
			availableFinanceSetting: [],
			paintName: '',
			trimName: '',
			disclaimer: '',
			disclaimerKey: '',
			combinedVehicleDisclaimers: '',
			offers: [],
			disableDisclaimer: false
		},
		dealer: {
			locationName: '',
			dealerName: '',
			dealerID: 0,
			stockDealerID: 0,
			queryDealerID: 0,
			dealerSites: [],
			lmct: '',
			region: '',
			state: '',
			nviUrl: '',
			website: '',
			depositMinimum: 0,
			cashDepositMaximum: 2000,
			callbackDays: [],
			openingHours: [],
			vehicleReturnGuarantee: false,
			privacyPolicy: '',
			storeWhyBuy: ''
		},
		agent: {
			firstName: '',
			lastName: '',
			email: '',
			phone: '',
			photoUrl: ''
		},
		finance: {
			isLoading: false,
			agencyFee: 0,
			apr: 0,
			comparisonRate: 0,
			DAFincGST: null,
			depositTotal: 0,
			nv: null,
			disclaimer: 0,
			disclaimerKey: 0,
			establishmentFee: 0,
			financeDeposit: 0,
			financeType: 0,
			gfv: 0,
			internalFinanceType: '',
			maxFinanceDeposit: 0,
			monthlyAccountFee: 0,
			monthlyPayment: 0,
			netAmountFinanced: 0,
			ppsrFee: 0,
			totalAmountFinanced: 0,
			product: 'Consumer Loan Access',
			weeklyPayment: 0,
			financeHeading: '',
			cmpFrom: 0,
			cmpTo: 0,
			creditScore: 0,
			isTpr: null,
			tprDifference: null,
			rateExpiry: '',
			balloon: 0,
			balloonPercentage: null,
			wasFinanced: null,
			dafQuantum: 0
		},
		customer: {
			orderType: 1,
			companyName: '',
			abn: '',

			title: '',
			firstName: '',
			preferredName: '',
			lastName: '',
			dob: '',
			preferredDealership: '',
			phone: '',
			otpPhone: '',
			resumePhone: '',
			email: '',

			address: '',
			unitNo: '',
			streetNo: '',
			streetName: '',
			streetType: '',
			streetAddress: '',
			suburb: '',
			state: '',
			postcode: '',

			sa1Code: '',
			subPremise: '',

			privacyAccepted: false,
			financeLeadPostcode: '',
			financeLeadPrivacyAccepted: false,

			privacyAcceptedFallback: false,
			businessUse: false,

			privacyAcceptedTpr: false,
			livingStatus: '', // Own = 1, Purchasing = 2, Renting = 3, Boarding = 4, LivingWithParents = 5
			licenseOrigin: '',
			licenseNumber: '',
			licenseState: '',
			otherProperty: '',
			expectedPurchase: '',
			contactMethod: '',
			fsl: '',
			recaptcha: '',
			additionalComment: '',

			hasSubmittedLead: false

			// driverLicenseCountry: '',
			// driverLicenseState: ''
		},
		tpr: {
			leadId: '',
			detailIdentifier: '',
			tprEnabled: true,
			isTprDown: '',
			hasLoadedWithTpr: false
		},
		quoteCreate: JSON.parse(JSON.stringify(Helpers.blankQuoteCreate)),
		updateQuote: JSON.parse(JSON.stringify(Helpers.blankUpdateQuote)),
		updateRepayments: JSON.parse(JSON.stringify(Helpers.blankUpdateRepayments)),
		retrieveQuote: JSON.parse(JSON.stringify(Helpers.blankRetrieveQuote)),
		lastSavedQuote: JSON.parse(JSON.stringify(Helpers.blankLastSavedQuote)),
		financeApplication: {
			preCheckout: false,
			preCheckoutAttempted: false,
			preCheckoutCompleted: false,
			preCheckoutReturn: 'purchaseOptions',
			viewVersion: null,
			doLater: false,
			loadedFromLink: false,
			finishedLoadingFromLink: false,
			submitted: false,
			submissionError: false,
			privacyAccepted: false,
			receiptNumber: 0,
			dobday: '',
			dobmonth: '',
			dobyear: '',
			OrderFinance: { // actual order object
				OrderID: null,
				Order: null,
				OrderGuid: '',
				OrderHash: '',
				LoanType: 1,
				Applicant: {
					Title: '',
					FirstName: '',
					MiddleName: '',
					LastName: '',
					Telephone: '',
					AlternateTelephone: '',
					Email: '',
					DateOfBirth: '', // 2000-01-01
					CorrespondenceMethod: 1, // Electronic = 1, Paper = 2
					MailingAddress: {
						UnitNumber: '',
						StreetNumber: '',
						StreetName: '',
						StreetType: '',
						Address: '',
						Suburb: '',
						Postcode: '',
						State: ''
					},
					NumberOfDependents: 0,
					DriverLicenseNumber: '',
					DriverLicenseState: '',
					DriverLicenseExpiry: ''
				},
				ResidenceOwnershipInfo: {
					OwnershipStatus: '', // Own = 1, Purchasing = 2, Renting = 3, Boarding = 4, LivingWithParents = 5
					MortgageProvider: '',
					MonthlyMortgagePayment: 0,
					HomeValue: 0,
					BalanceOwingOnHomeMortgage: 0,
					LandlordName: '',
					MonthlyRent: 0
				},
				ResidenceInfo: [],
				EmploymentDetailInfo: [],
				OtherIncomes: [],
				Expenses: {
					Groceries: 0,
					Household: 0,
					Utilities: 0,
					VehiclesAndTransport: 0,
					Insurance: 0,
					EducationAndChildcare: 0,
					LeisureAndEntertainment: 0,
					Other: 0
				},
				Debts: [],
				Assets: [],
				Liabilities: [],
				FinanceStatus: 1,
				DealerFinanceStatus: 1,
				ForseeableChanges: null,
				ChangeReductionIncome: false,
				ChangeIncreaseInExpenses: false,
				ChangeOther: false,
				ChangeAdditionalInformation: '',
				MeetsRequirements: null,
				TermsAccepted: false
			}
		},
		tradeIn: {
			hasTradeIn: false,
			vehicleModel: '',
			year: '',
			km: '',
			comment: ''
		},
		tradeValuationApp: {
			tradeValuationEnabled: false,
			tradeValuationSubmitted: false,
			tradeValuationEnquiryId: null,
			apiAttempts: 0,
			step: 1,
			minYear: ((new Date()).getFullYear() - 10),
			// api: 'https://stagingvaluation.toyotainventory.com.au/api',
			// auth: 'https://stagingvaluation.toyotainventory.com.au/oAuth/token',
			// bearerToken: '',
			car: {
				vin: '',
				year: '',
				make: '',
				model: '',
				makeCode: '',
				modelCode: '',
				modelYears: [],
				variants: {},
				variant: '',
				modelName: '',
				image: '',
				nvic: '',
				kilometres: '',
				valuationData: null,
				formData: {
					carImage: "",
					carMake: "",
					carModel: "",
					carName: "",
					carNvic: "",
					carVIN: "",
					carVariant: "",
					carYear: "",
					inputSearch: "",
					inputValue: "",
					makeCode: "",
					mileage: "",
					modelCode: ""
				},
				optionData: {}
			}
		},
		promoCode: {
			Applied: false,
			PromoTypeName: '',
			Code: '',
			Email: '',
			Description: '',
			Disc: '',
			DiscSymbol: '',
			NeedsAuth: false,
			PromoType: 0,
			PromoValue: 0,
			SysDesc: '',
			Type: '',
			Valid: false,
			ValueDesc: '',
		},
		openModal: {
			id: '',
			title: '',
			message: '',
			video: false,
			size: '',
			buttons: []
		},
		disclaimers: [],
		dataLayer: {},
		forgeRockApp: {
			hasRestoredUrlValues: false,
			preApprovalStatus: undefined,
			isTokenExpired: false,
			tokens: {
				accessToken: "",
				idToken: "",
				refreshToken: "",
				tokenExpiry: 0
			},
			userState: {
				user: {
					sub: "",
					subname: "",
					anonymousUser: undefined
				}
			},
			formMessage: {
				text: '',
				level: '',
			},
			viewStep: ''
		},
	},
	mutations: {
		setIsPostingTpr(state, payload) {
            state.app.isPostingTpr = payload;
        },
		setIsFinanceLoading(state, payload) {
			applyFinanceLoading(state, payload);
        },
		setAppUseCurrentRouteName(state, payload) {
            state.app.currentRouteName = payload;
        },
		setAppUseEstimateInsteadOfAlpha(state, payload) {
            state.app.useEstimateInsteadOfAlpha = payload;
        },
		setAppViewCurrentQuote(state, payload) {
			state.app.viewCurrentQuote = payload;
		},
		setIsCustomerPhoneValidated(state, payload) {
            state.app.isCustomerPhoneValidated = payload;
        },
		setAppFinanceOptions(state, payload){
			state.app.financeType = payload.financeType;
			state.app.financeDeposit = payload.financeDeposit;
			state.app.loanTerm = payload.termsInYears;
			state.app.kmsPerYear = payload.kms;
            state.app.balloonPercentage = payload.balloonPercentage;
        },
		setAppFinanceDeposit(state, payload) {
            state.app.financeDeposit = payload;
        },
        setAppLoanTerm(state, payload) {
            state.app.loanTerm = payload;
        },
        setAppKmsPerYear(state, payload) {
            state.app.kmsPerYear = payload;
        },
        setAppBalloonPercentage(state, payload) {
            state.app.balloonPercentage = payload;
        },
        setAppOriginalFinanceType(state, payload) {
            state.app.originalFinanceType = payload;
        },
        setAppFinanceType(state, payload) {
            state.app.financeType = payload;
        },
		setAppIsOtpModalOpen(state, payload) {
			// the OTP modal is ForgeRock's; with the toggle off it can never open,
			// whoever asks (CheckView, TprForm, SlidersFinance, CheckoutFinance).
			// TprAdjust hides its own dialog while this is true, so a stray true
			// without a modal to show would blank that page
            state.app.isOtpModalOpen = !!state.app.forgerockEnabled && !!payload;
        },
		setAppHasBadOmniGuid(state, payload) {
            state.app.hasBadOmniGuid = payload;
        },
		setForgeRockApp(state, payload) {
			const pl = JSON.parse(JSON.stringify(payload));
			state.forgeRockApp = { ...state.forgeRockApp, ...pl };
		},
		expireForgeRockTokens(state) {
			state.forgeRockApp.isTokenExpired = true;
			state.forgeRockApp.tokens.accessToken = "";
			state.forgeRockApp.tokens.idToken = "";
			state.forgeRockApp.tokens.refreshToken = "";
			state.forgeRockApp.tokens.tokenExpiry = 0;

			state.forgeRockApp.userState.user.sub = "";
			state.forgeRockApp.userState.user.subname = "";
			state.forgeRockApp.userState.user.anonymousUser = undefined;
		},
		setTpr(state, payload) {
			Vue.set(state, 'tpr', {...state.tpr, ...payload} );
		},
		setQuoteCreate(state, payload) {
			if (payload.pegaCaseTracking) {
				payload.pegaCaseTracking = payload.pegaCaseTracking.replace('TPR-', '');
			}
			state.quoteCreate = { ...state.quoteCreate, ...payload };
			// bridge into the legacy tpr slice so existing consumers (lead CaseID,
			// &Personalised= param, RateDisclaimer validityDate, analytics
			// tpr_case_id, the flow getter, checkGuid) keep working untouched
			const tprBridge: any = {};
			if (payload.pegaCaseTracking) tprBridge.pegaCaseTracking = payload.pegaCaseTracking;
			if (payload.guestRateIdentifier) tprBridge.guestRateIdentifier = payload.guestRateIdentifier;
			if (payload.validityDate) tprBridge.validityDate = payload.validityDate;
			if (Object.keys(tprBridge).length) {
				state.quoteCreate = { ...state.quoteCreate, ...tprBridge };
			}
		},
		setUpdateQuote(state, payload) {
			Vue.set(state, 'updateQuote', payload);
		},
		setUpdateRepayments(state, payload) {
			Vue.set(state, 'updateRepayments', payload);
		},
		// Pure setter. Adopting the retrieved quote's finance settings is a
		// separate, explicit step (applyRetrievedFinanceSettings): this mutation is
		// also used just to flip the success flag (resetAlpha in TprAdjust and
		// SlidersFinance) and is re-run by handleSaveQuote, so a side effect here
		// kept overwriting whatever the user had just adjusted.
		setRetrieveQuote(state, payload) {
			Vue.set(state, 'retrieveQuote', payload);
		},
		// Adopt the finance settings a retrieved quote was saved with. Only commit
		// this where the retrieved quote is the source of truth (restore/retrieve
		// entry) — never around save, where the current settings must win so that
		// the difference is what gets pushed to alfa.
		applyRetrievedFinanceSettings(state, payload) {
			const settings = getRetrievedFinanceSettings(payload.response);
			console.log('applyRetrievedFinanceSettings', settings);
			// only keys that resolved to a usable number are present, so a value
			// the api didn't return can never land as undefined/NaN
			if (isUsableNumber(settings.financeType)) state.app.financeType = settings.financeType;
			if (isUsableNumber(settings.kmsPerYear)) state.app.kmsPerYear = settings.kmsPerYear;
			if (isUsableNumber(settings.loanTerm)) state.app.loanTerm = settings.loanTerm;
			if (isUsableNumber(settings.financeDeposit)) state.app.financeDeposit = settings.financeDeposit;
			if (isUsableNumber(settings.balloonPercentage)) state.app.balloonPercentage = settings.balloonPercentage;
		},
		setLastSavedQuote(state, payload) {
			Vue.set(state, 'lastSavedQuote', payload);
		},
		// writes an externally-known eligibility outcome into whichever quote
		// responses are loaded (tpr store.js setPreApproval); distinct from
		// setPreApproval below, which stores the PreApprovalStatus API object
		setPreApprovalOutcome(state, payload) {
			const updateCI = state.updateRepayments.alfaFinancialResponse.customerDomain.customerInteraction;
			const retrieveCI = state.retrieveQuote.response.customerDomain.customerInteraction;
			updateCI.quote.eligibilityOutcome = payload;
			retrieveCI.quote.eligibilityOutcome = payload;
			state.lastSavedQuote.eligibilityOutcome = payload;
		},
		setStatus(state, payload){
			console.log('setStatus1', payload);
			const updateCI = state.updateRepayments.alfaFinancialResponse.customerDomain.customerInteraction;
			const retrieveCI = state.retrieveQuote.response.customerDomain.customerInteraction;
			if (!updateCI.preApproval){
				updateCI.preApproval = {};// will it be reactive?
			}
			updateCI.preApproval.preApprovalStatus = payload;
			retrieveCI.preApproval.preApprovalStatus = payload;
			state.lastSavedQuote.preApprovalStatus = payload;
			console.log('setStatus2', payload);
		},
		resetOpenModal(state) {
			state.openModal.id = '';
			state.openModal.title = '';
			state.openModal.size = '';
			state.openModal.message = '';
			state.openModal.video = false;
			Vue.set(state.openModal, 'buttons', []);
		},
		setOpenModal(state, payload) {
			state.openModal.id = payload.id;
			state.openModal.title = payload.title ? payload.title : '';
			state.openModal.message = payload.message ? payload.message : '';
			state.openModal.video = !!payload.video;
			state.openModal.size = payload.size || '';
			Vue.set(state.openModal, 'buttons', payload.buttons ? [...payload.buttons] : []);
		},
		setError(state, payload) {
			if (state.openModal.id !== 'ErrorModal') {
				state.openModal = { ...state.openModal, id: 'ErrorModal', ...payload };
			}
		},
		setValueAdd(state, payload) {
			state.valueAdd.hasRetrievedData = true;
			state.valueAdd.data = payload;
		},
		setValueAddAnalytics(state, payload) {
			state.valueAdd.hasSentAnalytics = payload;
		},
		setPromoCode(state, payload) {
			state.promoCode = { ...state.promoCode, ...payload };
		},
		setFinance(state, payload) {
            var Oldnv = state.finance.nv;
			state.finance = { ...state.finance, ...payload };
			if (payload.nv === undefined || payload.nv === null || payload.nv === ''	) {
                state.finance.nv = Oldnv;
            }
		},
		setFinanceApplication(state, payload) {
			state.finance = { ...state.financeApplication, ...payload };
		},
		setVehicle(state, payload) {
			state.vehicle = { ...state.vehicle, ...payload };
		},
		setApp(state, payload) {
			const { isFinanceLoading, ...rest } = payload;
			state.app = { ...state.app, ...rest };
			if (isFinanceLoading !== undefined) {
				applyFinanceLoading(state, isFinanceLoading);
			}
		},
		setAgent(state, payload) {
			state.agent = { ...state.agent, ...payload };
		},
		setDealer(state, payload) {
			state.dealer = { ...state.dealer, ...payload };
		},
		setCustomer(state, payload) {
			state.customer = { ...state.customer, ...payload };
		},
		addDisclaimer(state, payload) {
			(state.disclaimers as any) = [...state.disclaimers, payload];
		},
		setDisclaimers(state, payload) {
			Vue.set(state, 'disclaimers', [...payload]);
		},
		setDataLayer(state, payload) {
			state.dataLayer = { ...state.dataLayer, ...payload };
		},
		setTradeIn(state, payload) {
			state.tradeIn = { ...state.tradeIn, ...payload };
		},
		setTradeApp(state, payload) {
			state.tradeValuationApp = { ...state.tradeValuationApp, ...payload };
		},
	},
	actions: {
		setForgeRockTimer({ state, commit }) {
			clearTimeout(forgeRockExpiryTimer);
			if (!state.forgeRockApp.tokens.tokenExpiry) return;
			const d = new Date(state.forgeRockApp.tokens.tokenExpiry);

			// d.setMinutes(d.getMinutes() - 59); // just for test
			// d.setSeconds(d.getSeconds() - 50); // just for test

			// expire early: with < tokenExpiryBufferMs left the token must not
			// be relied on for anything new
			const delay = Math.max(0, d.getTime() - tokenExpiryBufferMs - Date.now());
			if (isNaN(delay)) return;
			forgeRockExpiryTimer = setTimeout(() => {
				console.log('EXPIRED');
				commit('expireForgeRockTokens');
			}, delay);
			console.log('setForgeRockTimer', forgeRockExpiryTimer, d);
		},
		async doRetrieveQuote({ state, commit }) {
			let success = false;
			commit('setApp', { isFinanceLoading: true });
			const pegaCaseTracking = state.quoteCreate.pegaCaseTracking;
			const accessToken = state.forgeRockApp.tokens.accessToken || Helpers.getCookie('token');
			const res = await retrieveQuoteApi(pegaCaseTracking, accessToken);
			if (res && res.success) {
				commit('setRetrieveQuote', res);
				success = true;
			}
			else {
				commit('setError', {
					message: `Sorry, your quote could not be retrieved.`
				});
			}
			commit('setApp', { isFinanceLoading: false });
			return Promise.resolve(success);
		},
		async doUpdateQuote({ state, commit }) {
			let success = false;
			commit('setApp', { isFinanceLoading: true });
			const obj = getQuoteCreateObj(state, false);
			const res = await updateQuoteApi(obj);
			if (res && res.success) {
				success = true;
				commit('setUpdateQuote', res);
			}
			else {
				console.log('update quote failed');
			}
			commit('setApp', { isFinanceLoading: false });
			console.log('doUpdateQuote', success);
			return success;
		},
		async doUpdateRepayments({ state, commit, dispatch }) {
			// do updateRepayments and show alpha
			let success = false;
			commit('setApp', { isFinanceLoading: true });
			const obj = getUpdateRepaymentsObj(state, router.currentRoute);
			const res = await updateRepaymentsApi(obj);
			if (res && res.success) {
				commit('setUpdateRepayments', res);
				// the footer disclaimer must match the quote-driven (alpha) figures
				const alphaDisclaimer = getAlphaFinance(state).disclaimer;
				if (alphaDisclaimer) {
					dispatch('removeDisclaimer', 'finance');
					commit('addDisclaimer', {
						type: 'finance',
						for: 'finance',
						key: '',
						text: alphaDisclaimer
					});
				}
				success = true;
			}
			commit('setApp', { isFinanceLoading: false });
			return success;
		},
		async doLastSavedQuote({ state, commit }) {
			let success = false;
			commit('setApp', { isFinanceLoading: true });
			const pegaCaseTracking = state.quoteCreate.pegaCaseTracking;
			const omniGuid = Helpers.getCookie('omniguid') || "";
			const dealerId = state.dealer.dealerID;
			console.log('doLastSavedQuote', pegaCaseTracking, omniGuid, dealerId);
			if (!omniGuid || !pegaCaseTracking) {
				commit('setApp', { isFinanceLoading: false });
				return success;
			}
			const res = await lastSavedQuote(pegaCaseTracking, omniGuid, dealerId);
			if (res) {
				commit('setLastSavedQuote', res);
				success = true;
			}
			commit('setApp', { isFinanceLoading: false });
			return success;
		},
		setLoading({ commit }, payload) {
			if (payload) {
				commit('setOpenModal', { id: 'LoadingModal' });
			}
			else {
				commit('resetOpenModal');
			}
		},
		removeDisclaimer({ commit, state }, payload) {
			const disclaimers = state.disclaimers.filter(d => d['type'] != payload);
			commit('setDisclaimers', disclaimers);
		},
		addDisclaimer({ commit }, payload) {
			commit('addDisclaimer', payload);
		}
	},
	getters: {
		// quote-backed (alpha) figures are only shown on 04 Summary (step 3) and
		// the confirmation SummaryPage; earlier checkout steps (incl. 03 Payment)
		// always show the estimate.
		alphaStepReached: state => {
			return !(state.app.currentRouteName == 'CheckoutPage' && state.app.step < 3);
		},
		// whether the first RetrieveQuote may run as soon as the data and token
		// allow (App.doFirstRetrieve). Never on the checkout: there the retrieve
		// is tied to the flow instead - straight after CreateQuote/UpdateRepayments
		// in the create-quote flow (App.doTprSubmittedFlow), or on Continue from
		// 03 Payment into 04 Summary in the return flow (App.handleSaveQuote)
		canRetrieveOnLoad: state => {
			return state.app.currentRouteName != 'CheckoutPage';
		},
		useAlpha: (state, getters) => {
			return getUseAlpha(state) && getters.alphaStepReached;
		},
		alphaFinance: state => {
			return getAlphaFinance(state);
		},
		// "view current quote": present the saved (retrieved) quote instead of the
		// loaded vehicle/finance after an UpdateRepayments failure (tpr parity)
		viewCurrentQuote: state => {
			return !!state.app.viewCurrentQuote && !!state.retrieveQuote.success;
		},
		retrievedQuoteView: state => {
			const r = state.retrieveQuote?.response || {};
			const spec = r.vehicleDomain?.vehicleSpecification || {};
			return {
				vehicleHeading: [spec.vehicleYear, spec.vehicleModel, spec.vehicleModelVariant].filter(Boolean).join(' '),
				financeHeading: r.financeHeading || '',
				loanType: r.loanType || 0,
				expiryDate: r.customerDomain?.customerInteraction?.quote?.expiryDate || ''
			};
		},
		alphaOrderOverrides: state => {
			return getAlphaOrderOverrides(state);
		},
		authLevel: state => {
			const obj = state.forgeRockApp.tokens?.accessToken
			? jwtDecode(state.forgeRockApp.tokens.accessToken) : {};
            return (obj as any).auth_level || 0;
        },
		flow: (state, getters) => {
			if (state.app.paymentMethod == 'cash') {
				return 'cash';
			}
			else if (state.customer.orderType == 2 && state.finance.internalFinanceType == 'Campaign' && getters.isBusinessFinanceAvailable) {
				return 'business-campaign';
			}
			else if (state.customer.orderType == 2) {//orderType
				return 'business';
			}
			else if (state.app.financeType == 4) {
				if (state.finance.isTpr) {
					return 'fixed-rate';
				}
				else if (!state.quoteCreate.guestRateIdentifier) {
					return 'get-tpr';
				}
				else {
					return 'indicative';
				}
			}
			else if (state.finance.internalFinanceType == 'Campaign') {
				return 'campaign';
			}
			else if (state.finance.internalFinanceType == 'Indicative' && state.tpr.tprEnabled) {
				if (state.finance.isTpr) {
					return 'adjust-tpr';
				}
				else if (!state.tpr.isTprDown) {
					return 'get-tpr';
				}
				else {
					return 'indicative';
				}
			}
			else if (state.finance.internalFinanceType == 'Indicative') {
				return 'indicative';
			}
		},
		// both business variants: no ForgeRock re-verification (OTP / Check
		// redirect) must ever interrupt them (independently of forgerockEnabled)
		isBusinessFlow: (state, getters: any) => {
			return 'business' == getters.flow || 'business-campaign' == getters.flow;
		},
		forgerockEnabled: state => {
			return !!state.app.forgerockEnabled;
		},
		captchaSiteKey(): string {
			return ((window as any).AppMode === 'app' || (window as any).AppMode === 'stagingapp') ? '6LfjD8EUAAAAAHWKFtKD1Yxx7HZWzOafhrUjBSbR' : '6LfN8bEUAAAAAHycQXBxTvtI-n86hl6ENhyVXqke';
		},
		isBusinessFinanceAvailable: state => {
			return !!state.vehicle.availableFinanceSetting.filter(o => (o as any).TypeOfFinance == 2).length;
		},
		isPersonalFinanceAvailable: state => {
			return !!state.vehicle.availableFinanceSetting.filter(o => (o as any).TypeOfFinance == 1).length;
		},
		amountChargedToday: state => {
			return state.app.paymentMethod === 'finance' ? state.app.financePayToday : state.app.cashDeposit;
		},
		vehicleFinalDeposit: state => {
			return state.app.paymentMethod === 'cash' ? state.app.cashDeposit : state.app.financePayToday;
		},
		vehicleFinalPrice: state => {
			const offers: Array<IOffersItem> = state.vehicle.offers.filter((o: IOffersItem) => o.offerType == EOfferType.VehiclePrice);

			if (offers.length) {
				return offers[0].price;
			}
			else if (state.promoCode.Applied && state.promoCode.PromoType == 1) {
				let discount: number = parseFloat(state.promoCode.PromoValue as any) || 0;
				if (isNaN(discount)) discount = 0;
				return state.vehicle.price - discount;
			}
			else {
				return state.vehicle.price;
			}
		},
		showPriceDisclaimer: state => {
			return !state.vehicle.disableDisclaimer
		}
	}
})
